import React, { useEffect, useState, useRef } from 'react';
import { DimmingOverlay } from './DimmingOverlay';

interface TypingMessageProps {
  text: string;
  onComplete: () => void;
}

const stageClasses = {
  'isTyping': 'demo-chat-message--human',
  'isSent': 'demo-chat-message--human sent',
} as const;

export const TypingMessage: React.FC<TypingMessageProps> = ({ text, onComplete }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [isSent, setIsSent] = useState(false);
  const [showDimming, setShowDimming] = useState(false);
  const messageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let currentIndex = 0;
    let typingTimeout: NodeJS.Timeout;
    let completionTimeout: NodeJS.Timeout;

    const typeNextChar = () => {
      if (currentIndex >= text.length) {
        setIsTyping(false);
        setIsSent(true);

        // Show dimming overlay when message is sent
        setShowDimming(true);

        // Set a separate timeout for completion
        completionTimeout = setTimeout(() => {
          onComplete();
        }, 3000);
        return;
      }

      const char = text[currentIndex];
      const prevChar = text[currentIndex - 1];
      setDisplayedText(prev => prev + char);
      currentIndex++;

      const baseDelay = 50;
      const randomDelay = Math.random() * 10 + 5;
      const extraWhitespaceDelay = Math.random() * 70;
      const extraPunctuationDelay = Math.random() * 200;
      const isWhitespace = /\s/.test(char);

      let delay = baseDelay + randomDelay;
      if (isWhitespace) {
        delay += extraWhitespaceDelay;
      }
      if (char === '.' || char === '!' || char === '?') {
        delay += extraPunctuationDelay;
      }
      if (char === prevChar) {
        delay = 7; // typing the same char is faster
      }

      typingTimeout = setTimeout(typeNextChar, delay);
    };

    typeNextChar();

    return () => {
      if (typingTimeout) clearTimeout(typingTimeout);
      if (completionTimeout) clearTimeout(completionTimeout);
    };
  }, [text]);

  const stageClass = isTyping ? stageClasses.isTyping : isSent ? stageClasses.isSent : '';

  return (
    <>
      <div
        ref={messageRef}
        className={`chat-message ${stageClass}`}
        style={{
          position: 'relative',
          zIndex: isSent ? 10 : 1, // Higher z-index to avoid overlapping
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isSent && <DimmingOverlay active={showDimming} duration={2000} />}

        {/* Content wrapper with even higher z-index to keep text visible */}
        <div style={{
          position: 'relative',
          zIndex: 15,  // Higher than dimming overlay
        }}>
          <div className="chat-message__content">
            {displayedText}
            {isTyping ? (
              <span className="chat-message__cursor">|</span>
            ) : (
              <span className="chat-message__cursor"> </span> // preserve spacing
            )}
          </div>
        </div>
      </div>
    </>
  );
};
